import { useEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";

function randInRange(min, max) {
  return Math.random() * (max - min) + min;
}

function makeCandleAround(target, jitter = 0.004) {
  // jitter = ~±1.5% autour de la cible
  const open = target * (1 + (Math.random() - 0.5) * jitter * 2);
  const close = target * (1 + (Math.random() - 0.5) * jitter * 2);
  const high = Math.max(open, close) * (1 + Math.random() * jitter);
  const low = Math.min(open, close) * (1 - Math.random() * jitter);
  return { open, high, low, close };
}

/**
 * Props :
 * - updateIntervalMs (par défaut 3000 ms)
 * - rangeNormal: [min, max] (par défaut [25, 35])
 * - rangeCrash:  [min, max] (par défaut [5, 10])
 * - history: nombre de bougies à initialiser (par défaut 50)
 * - buffer: longueur max des bougies conservées (par défaut 200)
 */
export default function LightweightCandles({
  updateIntervalMs = 3000,
  rangeNormal = [25, 35],
  rangeCrash = [5, 10],
  history = 50,
  buffer = 200,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const timerRef = useRef(null);
  const [mode, setMode] = useState("normal"); // "normal" | "crash"

  // Helper: détruit proprement le chart
  const destroyChart = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (chartRef.current) {
      chartRef.current.remove(); // détruit l'instance
      chartRef.current = null;
      seriesRef.current = null;
    }
  };

  // (Re)création du chart
  const initChart = () => {
    destroyChart();

    const chart = createChart(containerRef.current, {
      autoSize: true, // s'adapte au conteneur (ResizeObserver)
      layout: { background: { type: "Solid", color: "#0b0b0c" }, textColor: "#d7d7db" },
      grid: { vertLines: { color: "rgba(255,255,255,0.05)" }, horzLines: { color: "rgba(255,255,255,0.05)" } },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.1)" },
      timeScale: { borderColor: "rgba(255,255,255,0.1)" },
      crosshair: { mode: 0 },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#26a69a",
      downColor: "#ef5350",
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
      borderVisible: false,
    });

    // Seed historique
    const nowSec = Math.floor(Date.now() / 1000);
    const data = [];
    let target = randInRange(...rangeNormal);
    for (let i = history; i > 0; i--) {
      // on recule d’1 "unité de temps" par bougie (ici 1s pour la démo)
      const t = nowSec - i;
      // petit drift vers la plage courante
      const range = mode === "crash" ? rangeCrash : rangeNormal;
      const drift = randInRange(range[0], range[1]);
      target = (target * 0.8) + (drift * 0.2);
      const c = makeCandleAround(target);
      data.push({ time: t, open: c.open, high: c.high, low: c.low, close: c.close });
    }
    candleSeries.setData(data);
    chart.timeScale().fitContent();

    chartRef.current = chart;
    seriesRef.current = candleSeries;

    // Boucle d’update
    timerRef.current = setInterval(() => {
      const last = data[data.length - 1] ?? { close: randInRange(...rangeNormal), time: Math.floor(Date.now() / 1000) };
      const range = mode === "crash" ? rangeCrash : rangeNormal;
      // cible aléatoire dans la plage
      const targetVal = randInRange(range[0], range[1]);

      // “glisse” vers la cible pour un mouvement plus naturel
      const nextTarget = last.close * 0.9 + targetVal * 0.1;
      const candle = makeCandleAround(nextTarget);

      const next = {
        time: last.time + 1,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
      };

      // Ajoute/Met à jour la dernière bougie (Lightweight Charts accepte update/add de cette manière)
      seriesRef.current.update(next); // temps réel via update() :contentReference[oaicite:1]{index=1}
      data.push(next);
      if (data.length > buffer) data.shift();
    }, updateIntervalMs);
  };

  useEffect(() => {
    initChart();
    return destroyChart;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // init au montage

  // Quand on change l’intervalle, le mode, ou les plages → on recrée le chart pour un état propre
  useEffect(() => {
    initChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateIntervalMs, mode, rangeNormal[0], rangeNormal[1], rangeCrash[0], rangeCrash[1], history, buffer]);

  return (
    <div style={{ inlineSize: "100%", blockSize: "100%", minHeight: 380, display: "grid", gridTemplateRows: "auto 1fr", gap: 12 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ opacity: 0.8 }}>Mode:</span>
        <button
          onClick={() => setMode("normal")}
          style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #2a2a2a", background: mode === "normal" ? "#1d1f24" : "transparent", color: "#eaeaea" }}
        >
          Normal
        </button>
        <button
          onClick={() => setMode("crash")}
          style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #2a2a2a", background: mode === "crash" ? "#1d1f24" : "transparent", color: "#eaeaea" }}
        >
          Crash
        </button>

        <button
          onClick={initChart}
          title="Détruire et recréer le chart (test de résilience)"
          style={{ marginInlineStart: "auto", padding: "6px 10px", borderRadius: 8, border: "1px solid #2a2a2a", background: "#111", color: "#eaeaea" }}
        >
          Restart chart
        </button>
      </div>

      <div ref={containerRef} style={{ inlineSize: "100%", blockSize: "100%" }} />
    </div>
  );
}
